import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { useFocusTrap } from '../hooks/useFocusTrap'
import { ApiError, apiJson } from '../lib/api'
import { listConversations, type Conversation } from '../lib/conversations'

/**
 * Komut paleti: sohbet ara ve gec (`Ctrl/⌘ + K`).
 *
 * ## Iki arama, TEK alan
 *
 * Kullanici "hangi sohbetti" veya "ne yazmistim" diye ariyor ve ikisini ayri
 * arayuzlere bolmek, dogru olani bulmak icin iki kez arama yapmasi demek
 * olurdu. Bu yuzden:
 *
 *   * **Baslik eslesmesi** - yerel, ANINDA. Sunucuya gitmiyor.
 *   * **Mesaj icerigi** - `GET /api/search` (FTS5), gecikmeli.
 *
 * Baslik sonuclari once geliyor cunku kullanici cogunlukla bildigi bir
 * sohbeti ariyor; icerik aramasi "nerede yazmistim" sorusu icin.
 *
 * ## Gecikme (debounce) SART
 *
 * Her tusa basista FTS5 sorgusu atmak, tek GPU'lu bir makinede SQLite yazma
 * kilidiyle yarisan gereksiz okumalar uretiyor. 200 ms yeterli.
 */

const DEBOUNCE_MS = 200

interface SearchHit {
  message_id: string
  conversation_id: string
  conversation_title: string
  role: string
  snippet: string
}

export function CommandPalette({ onClose }: { onClose(): void }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const container = useRef<HTMLDivElement>(null)
  const [query, setQuery] = useState('')
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [hits, setHits] = useState<SearchHit[]>([])
  const [searchDisabled, setSearchDisabled] = useState(false)
  const [active, setActive] = useState(0)

  useFocusTrap(container, { active: true, onClose })

  // Baslik aramasi icin listeyi bir kez cek. Sunucuya her tusta gitmek
  // yerine yerel filtreleme: liste zaten sidebar'da var ve 30 kayit.
  useEffect(() => {
    listConversations()
      .then((page) => setConversations(page.items))
      .catch(() => setConversations([]))
  }, [])

  // Icerik aramasi - gecikmeli.
  useEffect(() => {
    const trimmed = query.trim()
    if (trimmed.length < 2) {
      setHits([])
      return
    }
    const timer = setTimeout(() => {
      apiJson<{ items: SearchHit[] }>(`/api/search?q=${encodeURIComponent(trimmed)}`)
        .then((data) => setHits(data.items))
        .catch((cause) => {
          // 503 = arama devre disi (`enable_fulltext_search=false`). Bunu
          // hata olarak gostermek yanlis: baslik aramasi hala calisiyor.
          if (cause instanceof ApiError && cause.status === 503) setSearchDisabled(true)
          setHits([])
        })
    }, DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [query])

  const needle = query.trim().toLocaleLowerCase('tr')
  const titleMatches = needle
    ? conversations.filter((item) => item.title.toLocaleLowerCase('tr').includes(needle))
    : conversations.slice(0, 8)

  // Tek bir gezinebilir liste: baslik sonuclari once.
  const results = [
    ...titleMatches.map((item) => ({
      key: `c:${item.id}`,
      conversationId: item.id,
      primary: item.title || 'Yeni sohbet',
      secondary: null as string | null,
    })),
    ...hits.map((hit) => ({
      key: `m:${hit.message_id}`,
      conversationId: hit.conversation_id,
      primary: hit.conversation_title || 'Yeni sohbet',
      secondary: hit.snippet,
    })),
  ]

  // Sorgu degisince secim basa donuyor: aksi halde kullanici gormedigi bir
  // satiri Enter'la aciyor.
  useEffect(() => {
    setActive(0)
  }, [query])

  function open(conversationId: string) {
    navigate(`/c/${conversationId}`)
    onClose()
  }

  return createPortal(
    <div
      className="palette-backdrop"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        className="palette"
        role="dialog"
        aria-modal="true"
        aria-label={t('palette.ariaLabel')}
        ref={container}
        tabIndex={-1}
      >
        <input
          className="palette-input"
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault()
              setActive((current) => Math.min(results.length - 1, current + 1))
            } else if (event.key === 'ArrowUp') {
              event.preventDefault()
              setActive((current) => Math.max(0, current - 1))
            } else if (event.key === 'Enter') {
              event.preventDefault()
              const target = results[active]
              if (target) open(target.conversationId)
            }
          }}
          placeholder={t('palette.placeholder')}
          autoFocus
          // Tarayici oneri listesi acilir listeyi kapatiyor.
          autoComplete="off"
          aria-controls="palette-results"
          aria-activedescendant={results[active] ? `palette-${results[active].key}` : undefined}
        />

        <ul className="palette-results" id="palette-results" role="listbox">
          {results.map((result, index) => (
            <li
              key={result.key}
              id={`palette-${result.key}`}
              role="option"
              aria-selected={index === active}
            >
              <button
                type="button"
                className={index === active ? 'palette-row palette-row-active' : 'palette-row'}
                onClick={() => open(result.conversationId)}
                // Fare ile gezinme klavye secimini de guncelliyor: iki ayri
                // "aktif" gostergesi kafa karistirici.
                onMouseEnter={() => setActive(index)}
              >
                <span className="palette-title">{result.primary}</span>
                {result.secondary && (
                  <span className="palette-snippet">{stripMarks(result.secondary)}</span>
                )}
              </button>
            </li>
          ))}
        </ul>

        {results.length === 0 && query.trim().length > 0 && (
          <p className="palette-note">{t('palette.noResults')}</p>
        )}
        {searchDisabled && (
          <p className="palette-note">{t('palette.titlesOnly')}</p>
        )}
      </div>
    </div>,
    document.body,
  )
}

/**
 * FTS5 `snippet()` vurgu isaretlerini temizler.
 *
 * Sunucu `snippet(..., '[[', ']]', '…', 18)` cagiriyor, yani eslesme
 * `[[kelime]]` olarak isaretli geliyor. Bunu OLDUGU GIBI gostermek kullaniciya
 * ham kose parantezler gostermek demek - canli sunucuda tam olarak boyle
 * gorundu (`[[istanbul]] trafigi hakkinda`).
 *
 * HTML etiketleri de soyuluyor: sunucu bugun uretmiyor ama `snippet()`in
 * isaretleyicileri yapilandirilabilir ve mesaj icerigi GUVENILMEYEN veri -
 * onu HTML olarak render etmek soz konusu degil, ham etiket gostermek de
 * kotu.
 */
function stripMarks(snippet: string): string {
  return snippet.replace(/\[\[|\]\]/g, '').replace(/<\/?[a-z][^>]*>/gi, '')
}
